import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { Observable } from 'rxjs';
import { ICategory } from '../../../category/model/category.model';
import { CategoryService } from '../../../category/services/category.service';
import { environment } from '../../../environments/environment';
import { stripeAccount } from '../../../financials/model/stripe-account.model';
import { BillingService } from '../../../financials/services/billing.service';
import { ToastService } from '../../../shared/services/toast.service';
import { IUser } from '../../../user/model/user.model';
import { ICourse } from '../../models/course.model';
import { ICourseStatus } from '../../models/courseStatus.model';
import { CourseService } from '../../services/course.service';
import { CommonModule, DatePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrService } from 'ngx-toastr';
import { CalendarModule } from 'primeng/calendar';
import { ImageUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { LanguageService } from '../../../shared/services/language.services';
import { Location } from '@angular/common';
import { TranslocoDatePipe, TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { NewlineToBrPipe } from "../../../shared/utils/utils";


@Component({
    selector: 'clp-course-review',
    templateUrl: './courseReview.component.html',
    styleUrls: ['./courseReview.component.scss'],
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        TranslocoDatePipe,
        TranslocoCurrencyPipe,
        CalendarModule,
        NewlineToBrPipe
    ],
    providers: [
        CategoryService,
        CourseService,
        ToastrService,
        DatePipe,
        BrowserAnimationsModule,
        LanguageService,
        BillingService,
    ]
})
export class CourseReviewComponent {
  private _location = inject(Location);
  private categoryService = inject(CategoryService);
  private courseService = inject(CourseService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private config = inject(PrimeNGConfig);
  private billingService = inject(BillingService);
  @Output() nextStep = new EventEmitter<string | null>();

  ICourseStatus = ICourseStatus;
  courseStatuses: { value: ICourseStatus, label: string }[] = [];
  categories$: ICategory[] = [];
  courseForm!: FormGroup;
  course$!: Observable<ICourse>;
  id!: string | null;
  edit: boolean = false;
  user$!: Observable<IUser >;
  imageUrl: string | undefined;
  currentImageFile?: string;
  account$!: Observable<stripeAccount>;
  account!: stripeAccount;
  nextOrder$! : Observable<number>;

  ngOnInit(): void {
      this.id = this.route.snapshot.paramMap.get('id');
      this.edit = this.id !== undefined && this.id !== null && this.id !== '';

      this.imageUrl = undefined;
      this.currentImageFile = undefined;

      this.loadCourseData();
      this.initializeCourseStatuses();
      this.setPrimeNGConfig();
  }


  loadCourseData(): void {
    this.course$ = this.courseService.getCourse(this.id ?? undefined);
  }

  initializeCourseStatuses(): void {
      this.courseStatuses = Object.entries(ICourseStatus)
          .filter(([key, value]) => typeof value === 'number')
          .map(([key, value]) => ({ value: value as ICourseStatus, label: `course.enum.${key}` }));
  }

  setPrimeNGConfig(): void {
    this.config.setTranslation({
        accept: this.translate.instant("general.calendar.accept"),
        reject: this.translate.instant("general.calendar.reject"),
        dayNames: this.translate.instant("general.calendar.dayNames"),
        dayNamesShort: this.translate.instant("general.calendar.dayNamesShort"),
        dayNamesMin: this.translate.instant("general.calendar.dayNamesMin"),
        monthNames: this.translate.instant("general.calendar.monthNames"),
        monthNamesShort: this.translate.instant("general.calendar.monthNamesShort"),
        today: this.translate.instant("general.calendar.today"),
        clear: this.translate.instant("general.calendar.clear"),
        dateFormat: this.translate.instant("general.calendar.dateFormat"),
        weekHeader: this.translate.instant("general.calendar.weekHeader")
    });
  }

  getImageUrl(picture: string | undefined): string {
    if (!picture) {
      return environment.API_URL + '/posts/images/default.png';
    }
    return environment.API_URL + '/posts/images/' + picture;
  }

  getStatusLabel(status: number | undefined): string {
    return `course.enum.${status}`;
  }

  getStatusBadgeClass(status?: number): string {
    switch (Number(status)) {
      case 1:
        return 'border-slate-600 bg-slate-700/50 text-slate-200';
      case 2:
        return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';
      case 3:
        return 'border-violet-500/40 bg-violet-500/15 text-violet-300';
      case 4:
        return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
      default:
        return 'border-slate-600 bg-slate-700/50 text-slate-200';
    }
  }

  getLessonStatusClass(status?: number | string): string {
    const statusValue = typeof status === 'string' ? status.toLowerCase() : status;

    if (statusValue === 1 || statusValue === '1' || statusValue === 'draft') {
      return 'review-lesson-status-draft';
    }

    if (statusValue === 2 || statusValue === '2' || statusValue === 'published') {
      return 'review-lesson-status-published';
    }

    return 'review-lesson-status-unknown';
  }

  getVideoTypeLabel(videoType?: number | string): string {
    switch (Number(videoType)) {
      case 1:
        return 'YouTube';
      case 2:
        return 'Vimeo';
      default:
        return '-';
    }
  }
}


export const salesDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const salesStartDateDate = control.get('salesStartDate');
  const salesEndDateDate = control.get('salesEndDate');

  if (salesStartDateDate && salesEndDateDate) {
    const startDate = new Date(salesStartDateDate.value);
    const endDate = new Date(salesEndDateDate.value);

    if (startDate > endDate) {
      return { salesDateInvalid: true };
    }
  }

  return null;
};
